import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, Button, Typography, Card, notification, Spin } from 'antd';
import { LockOutlined, UserOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { acceptInvitation, verifyInvitationToken } from "../backendApi/backend";
import Header from '../components/HeaderLogin';
import Footer from '../components/LoginFooter';
import "./InvitationAccept.css";

const { Title, Text, Paragraph } = Typography;

interface InvitationData {
  email: string;
  first_name?: string;
  last_name?: string;
  organization?: string;
}

const InvitationAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setErrorMessage("Invalid invitation link");
        setVerifying(false);
        return;
      }

      try {
        const response = await verifyInvitationToken(token);
        setInvitationData(response);
        setVerifying(false);
      } catch (error: any) {
        console.error("Error verifying invitation token:", error);
        setErrorMessage(error?.response?.data?.detail || "Invalid or expired invitation link");
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (values: any) => {
    if (!token || !invitationData) {
      notification.error({
        message: 'Error',
        description: 'Invalid invitation data',
      });
      return;
    }

    if (values.password !== values.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (values.password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      await acceptInvitation(token, values.password);

      notification.success({
        message: 'Account Created',
        description: 'Your account has been created successfully. You can now log in.',
      });

      navigate('/login');
    } catch (error: any) {
      console.error("Error accepting invitation:", error);
      setErrorMessage(
        error?.response?.data?.detail || 
        error?.response?.data?.message || 
        'Failed to create account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
    <div className="mainFrame" style = {{minHeight: '100vh'}}>
      <div className="invitation-page">
        <Header />
        <div className="invitation-loading">
          <Spin size="large" />
          <Text className="loading-text">Verifying invitation...</Text>
        </div>
        <Footer />
      </div>
    </div>
    );
  }

  if (errorMessage && !invitationData) {
    return (
    <div className="mainFrame" style = {{minHeight: '100vh'}}>
      <div className="invitation-page">
        <Header />
        <div className="invitation-error">
          <Card className="error-card">
            <Title level={3} className="error-title">Invalid Invitation</Title>
            <Paragraph className="error-text">{errorMessage}</Paragraph>
            <Button type="primary" className="error-button" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Card>
        </div>
        <Footer />
      </div>
    </div>
    );
  }

  return (
    <div className="mainFrame" style = {{minHeight: '100vh'}}>
    <div className="invitation-page">
      <Header />
      <div className="invitation-container">
        <div className="invitation-form-wrapper">
          <Card className="invitation-card">
            <div className="invitation-header">
              <Title level={2} className="invitation-title">Accept Invitation</Title>
              <Paragraph className="invitation-subtitle">
                Complete your account setup for {invitationData?.email}
              </Paragraph>
            </div>

            <Form
              form={form}
              name="invitation_accept"
              onFinish={handleSubmit}
              autoComplete="off"
              layout="vertical"
              className="invitation-form"
            >
              <Form.Item
                label="Email"
                name="email"
                initialValue={invitationData?.email}
              >
                <Input
                  prefix={<UserOutlined />}
                  value={invitationData?.email}
                  disabled
                  size="large"
                  className="invitation-input"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Please input your password!' },
                  { min: 8, message: 'Password must be at least 8 characters long!' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Create a password"
                  size="large"
                  className="invitation-input password-input-custom"
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Confirm your password"
                  size="large"
                  className="invitation-input password-input-custom"
                />
              </Form.Item>

              {errorMessage && (
                <div className="invitation-error-message">
                  <Text type="danger">{errorMessage}</Text>
                </div>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  className="invitation-submit-button"
                  icon={<ArrowRightOutlined />}
                  block
                >
                  Create Account
                </Button>
              </Form.Item>
            </Form>

            <div className="invitation-footer">
              <Text className="footer-text">
                Already have an account?{' '}
                <Button type="link" className="footer-link" onClick={() => navigate('/login')}>
                  Sign in
                </Button>
              </Text>
            </div>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
    </div>
  );
};

export default InvitationAccept;
